import React, { useEffect, useMemo, useState,useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Area, AreaChart, PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Colors for charts
const COLORS = ['#272757', '#20B2AA', '#f59e0b', '#4A0404', '#10b981', '#6366f1', '#f97316', '#8b5cf6', '#06b6d4', '#14b8a6'];

// Month maps and names
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_MAP = {
  'JANUARY': 0,'JAN':0,'FEBRUARY':1,'FEB':1,'MARCH':2,'MAR':2,'APRIL':3,'APR':3,
  'MAY':4,'JUNE':5,'JUN':5,'JULY':6,'JUL':6,'AUGUST':7,'AUG':7,'SEPTEMBER':8,'SEP':8,'SEPT':8,
  'OCTOBER':9,'OCT':9,'NOVEMBER':10,'NOV':10,'DECEMBER':11,'DEC':11
};

// targeted parser for "13-NOVEMBER-2025" (also accepts "13-november-2025" or "13-NOV-2025" or "13 NOVEMBER 2025")
function parseMonthIndexFromEnrolledBatch_ExactFormat(raw) {
  if (!raw && raw !== 0) return null;
  const s = String(raw).trim();
  if (!s) return null;

  const tokens = s.split(/[-\s\/]+/).filter(Boolean);
  if (tokens.length < 2) return null;

  // prefer middle token if format is exactly 3 tokens
  let monthToken = null;
  if (tokens.length === 3) monthToken = tokens[1];
  else {
    monthToken = tokens.find(t => MONTH_MAP.hasOwnProperty(t.toString().toUpperCase()));
  }
  if (!monthToken) return null;
  const idx = MONTH_MAP[monthToken.toString().toUpperCase()];
  return typeof idx === 'number' ? idx : null;
}

// helper: extract year from EnrolledBatch like "13-NOVEMBER-2025"
function parseYearFromEnrolledBatch(raw) {
  if (!raw) return null;
  const s = String(raw);
  const m = s.match(/\b(20\d{2})\b/);
  if (m) return parseInt(m[1], 10);
  return null;
}

// robust amount parsing helper: strips commas, currency symbols, returns float
const parseAmount = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const s = String(v).trim();
  if (!s) return 0;
  const cleaned = s.replace(/[^0-9.\-]/g, '');
  const f = parseFloat(cleaned);
  return Number.isFinite(f) ? f : 0;
};

// CSV stringify helper (simple, handles primitive values; doesn't deeply flatten objects)
function objectArrayToCSV(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  const keys = Array.from(arr.reduce((set, item) => {
    Object.keys(item).forEach(k => set.add(k));
    return set;
  }, new Set()));
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const header = keys.join(',');
  const lines = arr.map(obj => keys.map(k => escape(obj[k])).join(','));
  return [header, ...lines].join('\n');
}

function DashboardPending() {
  const navigate = useNavigate();
  const location = useLocation();


    const dashboardRef = useRef(null); // attach to container to capture to dowmload pdf,png
  

  const token = localStorage.getItem('token');
  const email = localStorage.getItem('email');
  const role = localStorage.getItem('role');

  // lead states
  const [students, setStudents] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [filterMonth, setFilterMonth] = useState('ALL'); // 'ALL' or 0..11
  const [filterYear, setFilterYear] = useState('ALL');   // 'ALL' or 2025 etc.
  const [filterRole, setFilterRole] = useState('ALL');   // 'ALL' | 'STUDENT' | 'PROFESSIONAL'
  const [filterFirstInstallment, setFilterFirstInstallment] = useState('ALL'); // 'ALL'|'PAID'|'UNPAID'
  const [q, setQ] = useState(''); // free-text search across name/email/phone

  // verify user effect
  useEffect(() => {
    const verify = async () => {
      if (!token) return navigate('/login');
      try {
        const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/check-user`, { email, role, token });
        if (!res?.data?.success) {
          toast.error(res?.data?.message || 'Please login again');
          localStorage.clear();
          return navigate('/login');
        }
      } catch (err) {
        toast.error('Please login again');
        localStorage.clear();
        navigate('/login');
      }
    };
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname]);

  // heuristic function to classify a lead as student or professional
  const classifyLead = (r) => {
    if (!r || typeof r !== 'object') return 'professional';
    const toStr = (v) => (v === null || v === undefined ? '' : String(v).trim().toUpperCase());
    const fieldsToCheck = [
      r.LeadCategory, r.category, r.Type, r.type, r.role, r.Role, r.leadType, r.LeadType, r.LeadFor, r.lead_for,
      r.profile?.role, r.isStudent, r.isProfessional, r.student, r.professional, r.designation, r.title
    ];

    if (r.isStudent === true || r.student === true) return 'student';
    if (r.isProfessional === true || r.professional === true) return 'professional';

    const combined = fieldsToCheck.map(toStr).join(' ');
    if (combined.includes('STUDENT') || combined.includes('LEARNER') || combined.includes('FRESHER') || combined.includes('TRAINING')) return 'student';
    if (combined.includes('PROFESSIONAL') || combined.includes('WORK') || combined.includes('EMPLOYEE') || combined.includes('EXPERIENCED') || combined.includes('EMP') || combined.includes('PRO')) return 'professional';

    const emailStr = toStr(r.Email || r.email || r.contactEmail || r.contact_email || '');
    if (emailStr && (emailStr.includes('.edu') || emailStr.includes('student'))) return 'student';

    return 'professional';
  };

  useEffect(() => {
    const fetchAll = async () => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/pending-installment/all-pending-installments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const raw = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
        const arr = Array.isArray(raw) ? raw : [raw];

        const s = [];
        const p = [];
        arr.forEach((item, i) => {
          const key = item.Email ?? item.email ?? item._id ?? item.id ?? `lead-${i}`;
          const lead = { key, ...item };
          const cls = classifyLead(item);
          if (cls === 'student') s.push(lead);
          else p.push(lead);
        });

        setStudents(s.map((r, i) => ({ key: r.key ?? `s-${i}`, ...r })));
        setProfessionals(p.map((r, i) => ({ key: r.key ?? `p-${i}`, ...r })));
      } catch (err) {
        console.error(err);
        setError('Failed to fetch demo leads');
        toast.error('Failed to load demo leads');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [token, location.key]);

  // Combined array helper (UNFILTERED)
  const combinedLeads = useMemo(() => [...students, ...professionals], [students, professionals]);

  // derive available years from combinedLeads for the year dropdown
  const availableYears = useMemo(() => {
    const sety = new Set();
    combinedLeads.forEach(r => {
      const y = parseYearFromEnrolledBatch(r.EnrolledBatch ?? r.enrolledBatch ?? r.availabilityBatch ?? r.availability_batch ?? '');
      if (y) sety.add(y);
      else {
        const cr = r.createdAt ?? r.created_at ?? r.created;
        if (cr) {
          const d = new Date(cr);
          if (!isNaN(d)) sety.add(d.getFullYear());
        }
      }
    });
    return Array.from(sety).sort((a,b) => b-a);
  }, [combinedLeads]);

  // filteredLeads pipeline (applies global filters)
  const filteredLeads = useMemo(() => {
    const qLower = (q || '').trim().toLowerCase();

    const getMonthYear = (r) => {
      const raw = r.EnrolledBatch ?? r.enrolledBatch ?? r.availabilityBatch ?? r.availability_batch ?? '';
      const monthIdx = parseMonthIndexFromEnrolledBatch_ExactFormat(raw);
      let year = parseYearFromEnrolledBatch(raw);
      if (!year) {
        const created = r.createdAt ?? r.created_at ?? r.created;
        if (created) {
          const d = new Date(created);
          if (!isNaN(d)) year = d.getFullYear();
        }
      }
      return { monthIdx, year };
    };

    return combinedLeads.filter((r) => {
      // MONTH
      if (filterMonth !== 'ALL') {
        const { monthIdx } = getMonthYear(r);
        if (monthIdx !== filterMonth) return false;
      }
      // YEAR
      if (filterYear !== 'ALL') {
        const { year } = getMonthYear(r);
        if (year !== filterYear) return false;
      }
      // ROLE
      if (filterRole !== 'ALL') {
        const cls = classifyLead(r);
        if (filterRole === 'STUDENT' && cls !== 'student') return false;
        if (filterRole === 'PROFESSIONAL' && cls !== 'professional') return false;
      }
      // FIRST INSTALLMENT
      if (filterFirstInstallment !== 'ALL') {
        const raw = String(r.FirstInstallmentStatus ?? r.firstInstallmentStatus ?? r.first_installment_status ?? '').trim().toUpperCase();
        const isPaid = /\bPAID\b/.test(raw);
        const isUnpaid = /\bUNPAID\b/.test(raw) || raw === '';
        if (filterFirstInstallment === 'PAID' && !isPaid) return false;
        if (filterFirstInstallment === 'UNPAID' && !isUnpaid) return false;
      }
      // TEXT search
      if (qLower) {
        const hay = [
          r.FullName ?? r.fullName ?? r.name ?? '',
          r.Email ?? r.email ?? '',
          r.Phone ?? r.phone ?? r.mobile ?? '',
          r.LeadSource ?? r.source ?? ''
        ].join(' ').toLowerCase();
        if (!hay.includes(qLower)) return false;
      }
      return true;
    });
  }, [combinedLeads, filterMonth, filterYear, filterRole, filterFirstInstallment, q]);

  // Today / Pending leads count (filtered)
  const totalPending = filteredLeads.length;

  // Top sources (filtered)
  const topSources = useMemo(() => {
    const counts = {};
    const normalize = (v) => (v || '').toString().trim().toUpperCase();
    filteredLeads.forEach((r) => {
      const raw = r.LeadSource || r.leadSource || r.source || r.Source || r.lead_source || r.SourceOfLead || '';
      const key = normalize(raw) || 'UNKNOWN';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({ name, count }))
      .sort((a,b) => b.count - a.count).slice(0,10);
  }, [filteredLeads]);

  // Student vs Professional donut data (filtered)
  const typeBreakdown = useMemo(() => {
    return [
      { name: 'Students', value: filteredLeads.filter(l => classifyLead(l) === 'student').length },
      { name: 'Professionals', value: filteredLeads.filter(l => classifyLead(l) === 'professional').length },
    ];
  }, [filteredLeads]);

  // Batch day aggregation (filtered)
  const batchDayData = useMemo(() => {
    const counts = { MORNING: 0, EVENING: 0, UNKNOWN: 0 };
    filteredLeads.forEach((r) => {
      const raw = r.BatchDay || r.batchDay || r.batch_day || r.batch || r.AvailabilityBatch || '';
      const key = (raw || '').toString().trim().toUpperCase();
      if (!key) counts.UNKNOWN += 1;
      else if (key.includes('MORN')) counts.MORNING += 1;
      else if (key.includes('EVE')) counts.EVENING += 1;
      else counts.UNKNOWN += 1;
    });

    const arr = [
      { name: 'MORNING', count: counts.MORNING },
      { name: 'EVENING', count: counts.EVENING },
    ];
    if (counts.UNKNOWN > 0) arr.push({ name: 'UNKNOWN', count: counts.UNKNOWN });
    return arr;
  }, [filteredLeads]);

  // --- Month-wise (Jan..Dec) aggregation for filteredLeads (EnrolledBatch or fallback to createdAt) ---
  const monthWiseData = useMemo(() => {
    const buckets = new Array(12).fill(0);
    filteredLeads.forEach(r => {
      let m = parseMonthIndexFromEnrolledBatch_ExactFormat(r.EnrolledBatch ?? r.enrolledBatch ?? r.availabilityBatch ?? r.availability_batch ?? '');
      if (m == null) {
        const created = r.createdAt ?? r.created_at ?? r.created;
        if (created) {
          const d = new Date(created);
          if (!isNaN(d)) m = d.getMonth();
        }
      }
      if (m != null && !isNaN(m) && m >= 0 && m <= 11) buckets[m] += 1;
    });
    return MONTH_NAMES.map((name, idx) => ({ name: name.slice(0,3).toUpperCase(), count: buckets[idx] }));
  }, [filteredLeads]);

  // --- FIRST / PENDING / TOTAL revenue aggregations (filtered) ---
  const firstInstallmentTotals = useMemo(() => {
    let total=0, junior=0, senior=0;
    filteredLeads.forEach(r => {
      const amt = parseAmount(
        r.FirstInstallmentAmount ?? r.firstInstallmentAmount ?? r.first_installment_amount ??
        r.First_Installment_Amount ?? r.FirstInstallmentAmountPaid ?? r.FirstInstallment ?? r.Amount ?? r.amount ?? 0
      );
      total += amt;
      const enrollment = (r.EnrollmentType || r.enrollmentType || r.enrollment_type || r.Type || r.type || r.Course || r.course || '').toString().trim().toUpperCase();
      if (enrollment.includes('JUNIOR')) junior += amt;
      else if (enrollment.includes('SENIOR')) senior += amt;
      else {
        const text = ((r.title || r.name || r.FullName || r.comment || r.notes) || '').toString().toUpperCase();
        if (text.includes('JUNIOR')) junior += amt;
        else if (text.includes('SENIOR')) senior += amt;
      }
    });
    return { total: Math.round(total*100)/100, junior: Math.round(junior*100)/100, senior: Math.round(senior*100)/100 };
  }, [filteredLeads]);

  const firstInstallmentChart = useMemo(() => ([
    { name: 'Junior', amount: firstInstallmentTotals.junior },
    { name: 'Senior', amount: firstInstallmentTotals.senior },
  ]), [firstInstallmentTotals]);

  const pendingInstallmentTotals = useMemo(() => {
    let total=0, junior=0, senior=0;
    filteredLeads.forEach(r => {
      const amt = parseAmount(
        r.BalanceInstallmentAmount ??
        r.Balance_Installment_Amount ??
        r.balanceInstallmentAmount ??
        r.balance_installment_amount ??
        r.BalanceInstallment ?? r.Balance ?? r.BalanceAmount ?? r.balanceAmount ?? 0
      );
      total += amt;
      const enrollment = (r.EnrollmentType || r.enrollmentType || r.enrollment_type || r.Type || r.type || r.Course || r.course || '').toString().trim().toUpperCase();
      if (enrollment.includes('JUNIOR')) junior += amt;
      else if (enrollment.includes('SENIOR')) senior += amt;
      else {
        const text = ((r.title || r.name || r.FullName || r.comment || r.notes) || '').toString().toUpperCase();
        if (text.includes('JUNIOR')) junior += amt;
        else if (text.includes('SENIOR')) senior += amt;
      }
    });
    return { total: Math.round(total*100)/100, junior: Math.round(junior*100)/100, senior: Math.round(senior*100)/100 };
  }, [filteredLeads]);

  const pendingInstallmentChart = useMemo(() => ([
    { name: 'Junior', amount: pendingInstallmentTotals.junior },
    { name: 'Senior', amount: pendingInstallmentTotals.senior },
  ]), [pendingInstallmentTotals]);

  const totalRevenueTotals = useMemo(() => {
    let total=0, junior=0, senior=0;
    filteredLeads.forEach(r => {
      const amt = parseAmount(
        r.NegotiatedFee ?? r.Negotiated_Fee ?? r.negotiatedFee ?? r.negotiated_fee ??
        r.TotalFee ?? r.totalFee ?? r.Fee ?? r.fee ?? 0
      );
      total += amt;
      const enrollment = (r.EnrollmentType || r.enrollmentType || r.enrollment_type || r.Type || r.type || r.Course || r.course || '').toString().trim().toUpperCase();
      if (enrollment.includes('JUNIOR')) junior += amt;
      else if (enrollment.includes('SENIOR')) senior += amt;
      else {
        const text = ((r.title || r.name || r.FullName || r.comment || r.notes) || '').toString().toUpperCase();
        if (text.includes('JUNIOR')) junior += amt;
        else if (text.includes('SENIOR')) senior += amt;
      }
    });
    return { total: Math.round(total*100)/100, junior: Math.round(junior*100)/100, senior: Math.round(senior*100)/100 };
  }, [filteredLeads]);

  const totalRevenueChart = useMemo(() => ([
    { name: 'Junior', amount: totalRevenueTotals.junior },
    { name: 'Senior', amount: totalRevenueTotals.senior },
  ]), [totalRevenueTotals]);

  // Other computed pieces left mostly unchanged but using filteredLeads...
  const enrollmentTypeData = useMemo(() => {
    const counts = { 'JUNIOR PENETRATION TESTER': 0, 'SENIOR PENETRATION TESTER': 0 };
    filteredLeads.forEach((r) => {
      const raw = (r.EnrollmentType || r.enrollmentType || r.enrollment_type || '').toString().trim().toUpperCase();
      if (raw.includes('JUNIOR')) counts['JUNIOR PENETRATION TESTER'] += 1;
      else if (raw.includes('SENIOR')) counts['SENIOR PENETRATION TESTER'] += 1;
    });
    return Object.entries(counts).map(([name,value])=>({name,value}));
  }, [filteredLeads]);

  const callStatusData = useMemo(() => {
    const counts = { REQUIRED: 0, PERFORMED: 0, "NOT REQUIRED": 0};
    filteredLeads.forEach((r) => {
      const raw = (r.CallStatus || r.callStatus || r.call_status || '').toString().trim().toUpperCase();
      if (raw.includes('REQUIRED')) counts.REQUIRED += 1;
      else if (raw.includes('PERFORMED') && !raw.includes('NOT')) counts.PERFORMED += 1;
      else if (raw.includes('NOT REQUIRED')) counts["NOT REQUIRED"] += 1;
      else counts.REQUIRED += 1;
    });
    return Object.entries(counts).map(([name,value])=>({name,value}));
  }, [filteredLeads]);

  const secondInstallmentData = useMemo(() => {
    const counts = { PAID: 0, UNPAID: 0 };
    filteredLeads.forEach((r) => {
      const raw = (r.SecondInstallmentStatus || r.firstInstallmentStatus || r.first_installment_status || '')
        .toString().trim().toUpperCase();
      if (/^\bUNPAID\b$/.test(raw) || raw.includes('UNPAID')) counts.UNPAID += 1;
      else if (/^\bPAID\b$/.test(raw) || raw.includes('PAID')) counts.PAID += 1;
      else counts.UNPAID += 1;
    });
    const total = counts.PAID + counts.UNPAID || 1;
    const paidPct = Math.round((counts.PAID / total) * 100);
    const unpaidPct = 100 - paidPct;
    const stacked = [{ name: 'Second Installment', PAID: counts.PAID, UNPAID: counts.UNPAID, paidPct, unpaidPct, total }];
    return { counts, stacked, paidPct, unpaidPct, total };
  }, [filteredLeads]);

  const remainderStatusData = useMemo(() => {
    const counts = { PENDING: 0, SENT: 0 };
    const normalize = (v) => {
      if (v === null || v === undefined) return '';
      if (typeof v === 'boolean') return v ? 'SENT' : 'PENDING';
      return String(v).trim().toUpperCase();
    };
    filteredLeads.forEach((r) => {
      const raw =
        normalize(r.Remainder_1) ||
        normalize(r.Remainder1) ||
        normalize(r.remainder_1) ||
        normalize(r.remainder1) ||
        normalize(r.reminder_1) ||
        normalize(r.reminder1) ||
        normalize(r.remainder_1_status) ||
        normalize(r.reminder_status) ||
        '';
      if (/\bSENT\b/.test(raw) || raw === 'YES' || raw === 'Y' || raw === '1') counts.SENT += 1;
      else counts.PENDING += 1;
    });
    const total = Math.max(1, counts.PENDING + counts.SENT);
    const pctSent = Math.round((counts.SENT / total) * 100);
    const pctPending = 100 - pctSent;
    return { counts, total, pct: { sent: pctSent, pending: pctPending } };
  }, [filteredLeads]);

  const warningMessageStatus = useMemo(() => {
    const norm = (v) => {
      if (v === null || v === undefined) return '';
      if (typeof v === 'boolean') return v ? 'SENT' : 'PENDING';
      return String(v).trim().toUpperCase();
    };
    const tryParse = (v) => {
      if (!v && v !== 0) return null;
      if (v instanceof Date && !isNaN(v)) return v;
      const s = String(v || '').trim();
      if (!s) return null;
      const d = new Date(s);
      if (!isNaN(d.getTime())) return d;
      const alt = s.replace(/\./g,'/').replace(/-/g,'/');
      const parts = alt.split('/');
      if (parts.length === 3) {
        if (parts[2].length === 4) {
          const dd = parseInt(parts[0],10), mm = parseInt(parts[1],10)-1, yy = parseInt(parts[2],10);
          const dt = new Date(yy,mm,dd);
          if (!isNaN(dt)) return dt;
        }
        if (parts[0].length === 4) {
          const yy = parseInt(parts[0],10), mm = parseInt(parts[1],10)-1, dd = parseInt(parts[2],10);
          const dt = new Date(yy,mm,dd);
          if (!isNaN(dt)) return dt;
        }
      }
      if (/^\d+$/.test(s)) {
        const dt = new Date(parseInt(s,10));
        if (!isNaN(dt)) return dt;
      }
      return null;
    };

    let pending = 0, sent = 0;
    const days = 7;
    const today = new Date();
    const makeKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const dayList = [];
    for (let i = days - 1; i >= 0; i--) dayList.push(new Date(today.getFullYear(), today.getMonth(), today.getDate() - i));
    const buckets = {};
    dayList.forEach(d => { buckets[makeKey(d)] = { day: d, PENDING: 0, SENT: 0 }; });

    filteredLeads.forEach(r => {
      const raw = r.WarningMessage ?? r.Warning_Message ?? r.warningMessage ?? r.warning_message ?? '';
      const s = norm(raw);
      const status = (/\bSENT\b/.test(s) || s === 'Y' || s==='YES' || s === '1') ? 'SENT' : 'PENDING';
      if (status === 'SENT') sent++; else pending++;
      const rawDate = r.WarningMessageDate ?? r.warningMessageDate ?? r.warning_date ?? r.createdAt ?? r.created_at ?? r.updatedAt;
      const parsed = tryParse(rawDate);
      let key = parsed ? makeKey(new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())) : makeKey(today);
      if (!buckets[key]) {
        const first = makeKey(dayList[0]), last = makeKey(dayList[dayList.length-1]);
        if (parsed) key = parsed < dayList[0] ? first : last;
        else key = last;
        if (!buckets[key]) buckets[key] = { day: parsed ? parsed : today, PENDING:0, SENT:0 };
      }
      buckets[key][status] = (buckets[key][status] || 0) + 1;
    });

    const trend = dayList.map(d => {
      const k = makeKey(d);
      const b = buckets[k] || { PENDING: 0, SENT: 0 };
      return { date: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), PENDING: b.PENDING, SENT: b.SENT, total: b.PENDING + b.SENT };
    });

    const total = Math.max(1, pending + sent);
    const pctSent = Math.round((sent / total) * 100);
    const pctPending = 100 - pctSent;
    const pieSeries = [{ name: 'Pending', value: pending }, { name: 'Sent', value: sent }];
    return { pending, sent, total: pending+sent, pctSent, pctPending, pieSeries, trend, days };
  }, [filteredLeads]);

  const studentStatusData = useMemo(() => {
    const norm = (v) => {
      if (v === null || v === undefined) return '';
      if (typeof v === 'boolean') return v ? 'ACTIVE' : 'INACTIVE';
      const s = String(v).trim().toUpperCase();
      if (s === '1' || s === 'YES' || s === 'Y' || s === 'ENROLLED' || s === 'REGISTERED' || s === 'ACTIVE') return 'ACTIVE';
      if (s === '0' || s === 'NO' || s === 'N' || s === 'NOT ENROLLED' || s === 'INACTIVE' || s === 'DROPPED') return 'INACTIVE';
      return s;
    };

    const tryParseDate = (v) => {
      if (!v && v !== 0) return null;
      if (v instanceof Date && !isNaN(v)) return v;
      const s = String(v || '').trim();
      if (!s) return null;
      const d = new Date(s);
      if (!isNaN(d.getTime())) return d;
      const alt = s.replace(/\./g,'/').replace(/-/g,'/');
      const parts = alt.split('/');
      if (parts.length === 3) {
        if (parts[2].length === 4) {
          const dd = parseInt(parts[0],10), mm = parseInt(parts[1],10)-1, yy = parseInt(parts[2],10);
          const dt = new Date(yy, mm, dd);
          if (!isNaN(dt)) return dt;
        }
        if (parts[0].length === 4) {
          const yy = parseInt(parts[0],10), mm = parseInt(parts[1],10)-1, dd = parseInt(parts[2],10);
          const dt = new Date(yy, mm, dd);
          if (!isNaN(dt)) return dt;
        }
      }
      if (/^\d+$/.test(s)) {
        const dt = new Date(parseInt(s,10));
        if (!isNaN(dt)) return dt;
      }
      return null;
    };

    let active=0, inactive=0;
    const days = 7;
    const today = new Date();
    const makeKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    const dayList = [];
    for (let i = days - 1; i >= 0; i--) dayList.push(new Date(today.getFullYear(), today.getMonth(), today.getDate() - i));
    const buckets = {};
    dayList.forEach(d => { buckets[makeKey(d)] = { ACTIVE: 0, INACTIVE: 0, day: d }; });

    filteredLeads.forEach((r) => {
      const raw =
        r.StudentStatus ?? r.studentStatus ?? r.student_status ??
        r.Status ?? r.status ??
        r.IsStudent ?? r.isStudent ?? r.student ??
        r.EnrollmentStatus ?? r.enrollment_status ?? r.enrolled ??
        '';
      const v = norm(raw);
      const isActive = v === 'ACTIVE' || v === 'ACTIVE' && v !== '';
      if (isActive) active += 1; else inactive += 1;
      const rawDate =
        r.StudentStatusDate ?? r.studentStatusDate ?? r.student_status_date ??
        r.createdAt ?? r.created_at ?? r.updatedAt ?? r.updated_at ?? null;
      const parsed = tryParseDate(rawDate);
      let key = parsed ? makeKey(new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())) : makeKey(today);
      if (!buckets[key]) {
        const first = makeKey(dayList[0]), last = makeKey(dayList[dayList.length-1]);
        if (parsed) key = parsed < dayList[0] ? first : last;
        else key = last;
        if (!buckets[key]) buckets[key] = { ACTIVE: 0, INACTIVE: 0, day: parsed ? parsed : today };
      }
      buckets[key][isActive ? 'ACTIVE' : 'INACTIVE'] = (buckets[key][isActive ? 'ACTIVE' : 'INACTIVE'] || 0) + 1;
    });

    const total = Math.max(1, active + inactive);
    const pctActive = Math.round((active / total) * 100);
    const pctInactive = 100 - pctActive;
    const pie = [{ name: 'Active', value: active }, { name: 'Inactive', value: inactive }];
    const radial = [{ name: 'active', value: pctActive }];
    const trend = dayList.map(d => {
      const k = makeKey(d);
      const b = buckets[k] || { ACTIVE: 0, INACTIVE: 0 };
      return { date: d.toLocaleDateString(undefined, { month:'short', day:'numeric' }), ACTIVE: b.ACTIVE, INACTIVE: b.INACTIVE, total: b.ACTIVE + b.INACTIVE };
    });

    return { counts: { active, inactive }, total: active+inactive, pct: { active: pctActive, inactive: pctInactive }, pie, radial, trend, days };
  }, [filteredLeads]);

  // --- Export CSV handler for filteredLeads ---
  const handleExportCSV = () => {
    try {
      // shallow flatten: convert objects/arrays to JSON strings so CSV doesn't break
      const normalized = filteredLeads.map(item => {
        const out = {};
        Object.keys(item).forEach(k => {
          const v = item[k];
          if (v === null || v === undefined) out[k] = '';
          else if (typeof v === 'object') out[k] = JSON.stringify(v);
          else out[k] = v;
        });
        return out;
      });
      const csv = objectArrayToCSV(normalized);
      if (!csv) {
        toast.error('No data to export');
        return;
      }
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g,'-');
      a.href = url;
      a.setAttribute('download', `pending_filtered_${ts}.csv`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully.');
    } catch (err) {
      console.error(err);
      toast.error('Export failed');
    }
  };

     // Export PNG
  const exportFilteredLeadsPNG = async () => {
    if (!dashboardRef.current) {
      toast.error('Dashboard not ready to export');
      return;
    }
    try {
      // scale for better resolution
      const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true, allowTaint: true });
      const dataUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `pending_installment_${(new Date()).toISOString().slice(0,10)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('PNG exported');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export PNG');
    }
  };
  
  // Export PDF (multi-page aware)
  const exportFilteredLeadsPDF = async () => {
    if (!dashboardRef.current) {
      toast.error('Dashboard not ready to export');
      return;
    }
    try {
      // render to canvas at higher scale
      const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true, allowTaint: true });
      const imgData = canvas.toDataURL('image/png');
  
      // A4 size in pt (points) for jsPDF
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
  
      // compute image dims to fit page width
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
  
      if (imgHeight <= pageHeight) {
        // single page
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        // multi-page: split vertically
        let remainingHeight = imgHeight;
        let position = 0;
        const canvasPageHeight = (canvas.height / imgHeight) * pageHeight; // approximate page height in canvas px
  
        // We'll slice the full-size canvas into pages by drawing portions to a temporary canvas
        const renderedCanvas = canvas;
        const scale = imgWidth / canvas.width; // scale factor used for img in PDF
        const pageCanvas = document.createElement('canvas');
        const pageCtx = pageCanvas.getContext('2d');
  
        const pageCanvasHeightPx = Math.floor(pageHeight / scale); // how many source px fit per pdf page
  
        pageCanvas.width = canvas.width;
        pageCanvas.height = pageCanvasHeightPx;
  
        let srcY = 0;
        while (srcY < renderedCanvas.height) {
          // draw portion
          pageCtx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
          pageCtx.drawImage(renderedCanvas, 0, srcY, renderedCanvas.width, pageCanvas.height, 0, 0, pageCanvas.width, pageCanvas.height);
          const pageDataUrl = pageCanvas.toDataURL('image/png');
  
          if (srcY > 0) pdf.addPage();
          pdf.addImage(pageDataUrl, 'PNG', 0, 0, imgWidth, (pageCanvas.height * imgWidth) / pageCanvas.width);
  
          srcY += pageCanvas.height;
        }
      }
  
      const fileName = `pending_installment_${(new Date()).toISOString().slice(0,10)}.pdf`;
      pdf.save(fileName);
      toast.success('PDF exported');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export PDF');
    }
  };
  

  return (
       <div ref={dashboardRef} className="p-6 space-y-6">
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        className="!mb-6 !ml-4"
        toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
      />

      {/* Filters + Export */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value,10))} className="px-3 py-2 border rounded">
          <option value="ALL">All months</option>
          {MONTH_NAMES.map((m, idx) => <option key={m} value={idx}>{m}</option>)}
        </select>

        <select value={filterYear} onChange={(e) => setFilterYear(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value,10))} className="px-3 py-2 border rounded">
          <option value="ALL">All years</option>
          {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="px-3 py-2 border rounded">
          <option value="ALL">All roles</option>
          <option value="STUDENT">Students</option>
          <option value="PROFESSIONAL">Professionals</option>
        </select>

        <select value={filterFirstInstallment} onChange={(e) => setFilterFirstInstallment(e.target.value)} className="px-3 py-2 border rounded">
          <option value="ALL">All payments</option>
          <option value="PAID">Paid</option>
          <option value="UNPAID">Unpaid</option>
        </select>

        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name/email/phone" className="px-3 py-2 border rounded flex-1 min-w-[220px]" />

        <button onClick={() => { setFilterMonth('ALL'); setFilterYear('ALL'); setFilterRole('ALL'); setFilterFirstInstallment('ALL'); setQ(''); }} className="px-3 py-2 rounded bg-slate-100">Reset</button>

       <div className="flex items-center gap-2 ml-auto">
          <button onClick={handleExportCSV} className="px-3 py-2 text-white rounded-md shadow-sm bg-emerald-600 hover:bg-emerald-700">
            Export CSV
          </button>

            {/* PNG export */}
<button onClick={exportFilteredLeadsPNG} className="px-3 py-2 ml-2 text-white rounded bg-sky-600 hover:bg-sky-700">Export PNG</button>

{/* PDF export */}
<button onClick={exportFilteredLeadsPDF} className="px-3 py-2 ml-2 text-white rounded bg-rose-600 hover:bg-rose-700">Export PDF</button>
      
     
         
        </div>
      </div>

      {/* Header section */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="grid grid-cols-1 gap-6">
          <div className="flex flex-col items-start justify-start col-span-1 p-4 bg-white shadow rounded-xl min-h-[100px]">
            <div className="flex items-center justify-between w-full mb-4">
              <h3 className="text-lg font-semibold text-black ">TOTAL PENDING INSTALLMENT LEADS</h3>
            </div>
            <div className="flex items-center justify-center w-10 h-10 mx-auto mt-4 rounded-md">
              <div className="text-center">
                <div className="mt-5 font-bold text-7xl">{loading ? '...' : totalPending}</div>
              </div>
            </div>
          </div>

          <div className="col-span-1 p-4 bg-white shadow lg:col-span-1 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-black">LEADS SOURCES</h3>
              <button className="text-xl font-bold text-black" onClick={() => window.location.reload()}>↻</button>
            </div>
            <div className="divide-y">
              {topSources.length === 0 && <div className="p-6 text-sm text-slate-400">No sources available</div>}
              {topSources.map((s, i) => (
                <div key={s.name} className="flex items-center justify-between py-3">
                  <div className="text-sm font-semibold text-black">{i + 1}. {s.name}</div>
                  <div className="text-sm font-semibold text-black">{s.count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enrollment type + month-wise small area */}
        <div className="col-span-1 p-4 space-y-4 bg-white shadow rounded-xl">
          <h3 className="mb-2 text-lg font-semibold text-black">ENROLLMENT TYPE</h3>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={enrollmentTypeData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={5}
                  outerRadius={80}
                  paddingAngle={1}
                  label={(entry) => `${entry.name} (${entry.value})`}
                >
                  {enrollmentTypeData.map((entry, index) => {
                    let color = '#94a3b8';
                    if (entry.name === 'JUNIOR PENETRATION TESTER') color = '#1e3a8a';
                    else if (entry.name === 'SENIOR PENETRATION TESTER') color = '#064e3b';
                    return <Cell key={`cell-${index}`} fill={color} />;
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Small month-wise bar chart (Jan-Dec) */}
          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-700">Month-wise (Jan → Dec)</h4>
            <div style={{ width: '100%', height: 180 }}>
              <ResponsiveContainer>
                <BarChart data={monthWiseData} margin={{ top: 6, right: 6, left: 6, bottom: 6 }}>
                  <CartesianGrid strokeDasharray="6 4" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" barSize={14}>
                    {monthWiseData.map((entry, idx) => <Cell key={`m-${idx}`} fill={idx === (new Date().getMonth()) ? '#2563eb' : '#7c3aed'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Admin widgets (Total Revenue, First Installment, Pending Installment) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {role === "ADMIN" && (
          <>
            <div className="col-span-1 p-4 bg-white shadow rounded-xl">
              <h3 className="mb-2 text-lg font-semibold text-black">TOTAL REVENUE — NEGOTIATED FEE</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 text-center bg-yellow-100 rounded-md">
                  <div className="text-md text-slate-600">Total Revenue</div>
                  <div className="mt-2 text-3xl font-bold">₹ { (totalRevenueTotals.total || 0).toLocaleString('en-IN') }</div>
                </div>
                <div className="p-3 text-center rounded-md bg-slate-100">
                  <div className="text-md text-slate-500">Junior Revenue</div>
                  <div className="mt-2 text-2xl font-semibold">₹ { (totalRevenueTotals.junior || 0).toLocaleString('en-IN') }</div>
                </div>
                <div className="p-3 text-center rounded-md bg-slate-100">
                  <div className="text-md text-slate-500">Senior Revenue</div>
                  <div className="mt-2 text-2xl font-semibold">₹ { (totalRevenueTotals.senior || 0).toLocaleString('en-IN') }</div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 mt-4">
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart data={totalRevenueChart} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="6 4" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(v) => [`₹ ${v.toLocaleString('en-IN')}`, "Amount"]} />
                      <Bar dataKey="amount" radius={[6,6,0,0]} barSize={40} label={{ position: 'top', formatter: (v) => `₹${v.toLocaleString('en-IN')}` }}>
                        {totalRevenueChart.map((entry, idx) => <Cell key={`rev-${idx}`} fill="#a3030b" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="col-span-1 p-4 bg-white shadow rounded-xl">
              <h3 className="mb-2 text-lg font-semibold text-black">FIRST INSTALLMENT — AMOUNT PAID</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 text-center bg-green-200 rounded-md">
                  <div className="text-md text-slate-500">Total Collected</div>
                  <div className="mt-2 text-3xl font-bold">₹ { (firstInstallmentTotals.total || 0).toLocaleString('en-IN') }</div>
                </div>
                <div className="p-3 text-center rounded-md bg-slate-100">
                  <div className="text-md text-slate-500">Junior</div>
                  <div className="mt-2 text-2xl font-semibold">₹ { (firstInstallmentTotals.junior || 0).toLocaleString('en-IN') }</div>
                </div>
                <div className="p-3 text-center rounded-md bg-slate-100">
                  <div className="text-md text-slate-500">Senior</div>
                  <div className="mt-2 text-2xl font-semibold">₹ { (firstInstallmentTotals.senior || 0).toLocaleString('en-IN') }</div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 mt-4">
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart data={firstInstallmentChart} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="6 4" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(v) => [`₹ ${v.toLocaleString('en-IN')}`, 'Amount']} />
                      <Bar dataKey="amount" radius={[6,6,0,0]} barSize={40} label={{ position: 'top', formatter: (v) => `₹${v.toLocaleString('en-IN')}` }}>
                        {firstInstallmentChart.map((entry, idx) => <Cell key={`pb-${idx}`} fill="#2563eb" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* ADDED: Pending Installment Amount card */}
            <div className="col-span-1 p-4 bg-white shadow rounded-xl">
              <h3 className="mb-2 text-lg font-semibold text-black">PENDING INSTALLMENT — AMOUNT (Balance Amount)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 text-center bg-blue-200 rounded-md">
                  <div className="text-md text-slate-500">Total Pending</div>
                  <div className="mt-2 text-3xl font-bold">₹ { (pendingInstallmentTotals.total || 0).toLocaleString('en-IN') }</div>
                </div>
                <div className="p-3 text-center rounded-md bg-slate-100">
                  <div className="text-md text-slate-500">Junior Pending</div>
                  <div className="mt-2 text-2xl font-semibold">₹ { (pendingInstallmentTotals.junior || 0).toLocaleString('en-IN') }</div>
                </div>
                <div className="p-3 text-center rounded-md bg-slate-100">
                  <div className="text-md text-slate-500">Senior Pending</div>
                  <div className="mt-2 text-2xl font-semibold">₹ { (pendingInstallmentTotals.senior || 0).toLocaleString('en-IN') }</div>
                </div>
              </div>

              {/* big blue bar chart */}
              <div className="grid grid-cols-1 gap-4 mt-9">
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={pendingInstallmentChart} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="6 4" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(v) => [`₹ ${v.toLocaleString('en-IN')}`, 'Amount']} />
                      <Bar dataKey="amount" radius={[6,6,0,0]} barSize={40} label={{ position: 'top', formatter: (v) => `₹${v.toLocaleString('en-IN')}` }}>
                        {pendingInstallmentChart.map((entry, idx) => <Cell key={`pb-p-${idx}`} fill="#089c2f" />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Lower row: Lead category, Batch day */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="col-span-1 p-4 bg-white shadow rounded-xl">
          <h3 className="mb-2 text-lg font-semibold text-black">LEAD CATEGORY</h3>
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={typeBreakdown} dataKey="value" nameKey="name" innerRadius={80} outerRadius={140} paddingAngle={1} label={(entry) => `${entry.name} (${entry.value})`}>
                  {typeBreakdown.map((entry, index) => {
                    let color = '#94a3b8';
                    if (entry.name === 'Students') color = '#0F059E';
                    else if (entry.name === 'Professionals') color = '#1B5349';
                    return <Cell key={`cell-${index}`} fill={color} />;
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2"><span className="w-3 h-3 bg-blue-800 rounded-full"></span><span className="text-slate-600">Students</span></div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 bg-green-800 rounded-full"></span><span className="text-slate-600">Professionals</span></div>
          </div>
        </div>

        <div className="p-6 bg-white shadow rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="mb-2 text-lg font-semibold text-black">BATCH DAY DISTRIBUTION</h3>
            <div className="text-sm text-black">Total: <span className="font-semibold">{batchDayData.reduce((a,b)=>a+b.count,0)}</span></div>
          </div>

          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={batchDayData} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
                <defs>
                  <linearGradient id="gradMorning" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="gradEvening" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f97316" stopOpacity={1} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="gradUnknown" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#94a3b8" stopOpacity={1} />
                    <stop offset="100%" stopColor="#64748b" stopOpacity={1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="6 4" horizontal={true} vertical={false} strokeWidth={2} stroke="#cbd5e1" />
                <XAxis dataKey="name" type="category" axisLine={{ stroke: '#cbd5e1', strokeWidth: 3, strokeDasharray: '6 4' }} tickLine={false} tick={{ fill: '#475569', fontSize: 12, fontWeight: 500 }} padding={{ left: 20, right: 20 }} />
                <YAxis domain={[0, 9]} allowDecimals={false} axisLine={false} tickLine={false} />
                <Tooltip formatter={(value) => [value, 'Count']} wrapperStyle={{ borderRadius: 8 }} />
                <Bar dataKey="count" name="Count" radius={[0, 0, 0, 0]} barSize={150} animationDuration={800}>
                  {batchDayData.map((entry, idx) => {
                    const id = entry.name === 'MORNING' ? 'gradMorning' : entry.name === 'EVENING' ? 'gradEvening' : 'gradUnknown';
                    return <Cell key={`cell-${idx}`} fill={`url(#${id})`} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="col-span-1 p-4 bg-white shadow-lg rounded-xl">
        <h3 className="mb-2 text-lg font-semibold text-black">PENDING PAYMENT LINKS TO SEND</h3>

        <div className="grid items-center grid-cols-1 gap-4 mt-10 md:grid-cols-3">
          <div className="md:col-span-3">
            <div className="flex items-center justify-between mb-6 ml-6">
              <div>
                <div className="text-sm text-slate-500">Total</div>
                <div className="text-2xl font-bold text-black">{remainderStatusData.total}</div>
              </div>
              <div className="text-sm text-slate-500">Distribution</div>
            </div>

            <div className="relative w-full h-12 overflow-hidden border rounded-full bg-slate-100 border-slate-200">
              <div
                style={{
                  width: `${remainderStatusData.pct.pending}%`,
                  background: 'linear-gradient(90deg,#f5443b,#b00f07)',
                  height: '100%',
                  float: 'left',
                  transition: 'width 600ms ease',
                }}
                title={`Pending: ${remainderStatusData.counts.PENDING} (${remainderStatusData.pct.pending}%)`}
              />

              <div
                style={{
                  width: `${remainderStatusData.pct.sent}%`,
                  background: 'linear-gradient(90deg,#34d399,#10b981)',
                  height: '100%',
                  float: 'left',
                  transition: 'width 600ms ease',
                }}
                title={`Sent: ${remainderStatusData.counts.SENT} (${remainderStatusData.pct.sent}%)`}
              />

              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="px-3 py-1 text-sm font-medium rounded-full bg-white/70">
                  {remainderStatusData.counts.SENT} Sent
                  <span className="mx-2 text-slate-400">/</span>
                  {remainderStatusData.total} total
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-3 md:col-span-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50">
              <div className="flex items-center justify-center w-10 h-10 rounded-md bg-amber-100">
                <svg className="w-5 h-5 text-red-200" viewBox="0 0 20 20" fill="currentColor"><path d="M3 3h14v2H3V3zM3 7h10v2H3V7zm0 4h14v6H3v-6z" /></svg>
              </div>
              <div>
                <div className="text-xs text-red-700">Pending</div>
                <div className="text-lg font-semibold text-amber-700">{remainderStatusData.counts.PENDING}</div>
                <div className="text-xs text-red-600">{remainderStatusData.pct.pending}%</div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50">
              <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-md">
                <svg className="w-5 h-5 text-green-600" viewBox="0 0 20 20" fill="currentColor"><path d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293A1 1 0 103.293 10.707l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"/></svg>
              </div>
              <div>
                <div className="text-xs text-slate-500">Sent</div>
                <div className="text-lg font-semibold text-green-700">{remainderStatusData.counts.SENT}</div>
                <div className="text-xs text-slate-400">{remainderStatusData.pct.sent}%</div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
              <div className="flex items-center justify-center w-10 h-10 rounded-md bg-slate-100">
                <svg className="w-5 h-5 text-slate-600" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a8 8 0 100 16 8 8 0 000-16zM9 7h2v5H9V7zM9 13h2v2H9v-2z"/></svg>
              </div>
              <div>
                <div className="text-xs text-slate-500">Total</div>
                <div className="text-lg font-semibold text-slate-800">{remainderStatusData.total}</div>
                <div className="text-xs text-slate-400">{remainderStatusData.counts.PENDING + remainderStatusData.counts.SENT} leads</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CALL STATUS */}
      <div className="col-span-1 p-4 bg-white shadow rounded-xl">
        <h3 className="mb-2 text-lg font-semibold text-black">CALL STATUS</h3>
        <div style={{ width: '100%', height: 360 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={callStatusData}
                dataKey="value"
                nameKey="name"
                innerRadius={80}
                outerRadius={140}
                paddingAngle={1}
                label={(entry) => `${entry.name} (${entry.value})`}
              >
                {callStatusData.map((entry, index) => {
                  let color = '#94a3b8';
                  if (entry.name === 'REQUIRED') color = '#721604';
                  else if (entry.name === 'PERFORMED') color = '#024519';
                  else if (entry.name === 'NOT REQUIRED') color = '#ef4444';
                  return <Cell key={`cell-${index}`} fill={color} />;
                })}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2"><span className="w-3 h-3 bg-red-800 rounded-full"></span><span className="text-slate-600">Required</span></div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 bg-green-800 rounded-full"></span><span className="text-slate-600">Performed</span></div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 bg-red-500 rounded-full"></span><span className="text-slate-600">Not Required</span></div>
        </div>
      </div>

      {/* SECOND INSTALLMENT STATUS — RADIAL + MINI-BARS */}
      <div className="col-span-1 p-4 bg-white shadow-lg rounded-2xl">
        <h3 className="mb-3 text-lg font-semibold text-black">SECOND INSTALLMENT STATUS</h3>

        <div className="flex flex-col items-center gap-6 mt-14 sm:flex-row">
          <div className="flex items-center justify-center">
            <div className="relative h-60 w-60">
              <svg viewBox="0 0 36 36" className="w-full h-full">
                <defs>
                  <linearGradient id="gPaid" x1="0" x2="1">
                    <stop offset="0%" stopColor="#34d399" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>

                <path
                  d="M18 2.0845
                     a 15.9155 15.9155 0 0 1 0 31.831
                     a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#e6edf3"
                  strokeWidth="4"
                />

                <path
                  d="M18 2.0845
                     a 15.9155 15.9155 0 0 1 0 31.831
                     a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="url(#gPaid)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={`${secondInstallmentData.paidPct} ${100 - secondInstallmentData.paidPct}`}
                  strokeDashoffset="25"
                  style={{ transition: 'stroke-dasharray 800ms ease' }}
                  aria-hidden="true"
                />
              </svg>

              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-lg font-semibold text-slate-800">{secondInstallmentData.paidPct}%</div>
                <div className="text-xs text-slate-400">PAID</div>

                <div className="mt-2 text-center">
                  <div className="text-sm text-slate-600">Paid <span className="font-bold text-green-700">{secondInstallmentData.counts.PAID}</span></div>
                  <div className="text-sm text-slate-600">Total <span className="font-bold">{secondInstallmentData.total}</span></div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 w-full">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm text-slate-500">Summary</div>
                <div className="text-lg font-bold text-black">{secondInstallmentData.total} leads</div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  className="px-3 py-2 text-sm font-semibold text-white transition bg-indigo-600 rounded-md shadow hover:bg-indigo-700"
                  onClick={() => navigate('/pending-installments')}
                >
                  Open Payments
                </button>
              </div>
            </div>

            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-green-50">
                    <svg className="w-4 h-4 text-green-700" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293A1 1 0 103.293 10.707l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"/></svg>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700">Paid</div>
                    <div className="text-xs text-slate-400">{secondInstallmentData.counts.PAID} leads</div>
                  </div>
                </div>
                <div className="text-sm font-semibold text-green-700">{secondInstallmentData.paidPct}%</div>
              </div>

              <div className="w-full h-12 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${secondInstallmentData.paidPct}%`,
                    background: 'linear-gradient(90deg,#34d399,#059669)',
                    transition: 'width 700ms ease',
                    boxShadow: 'inset 0 -2px 6px rgba(0,0,0,0.06)'
                  }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-rose-50">
                    <svg className="w-4 h-4 text-rose-700" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11V5a1 1 0 10-2 0v2a1 1 0 00.293.707L10 9l.707-.293A1 1 0 0011 7zM9 12a1 1 0 012 0v3a1 1 0 11-2 0v-3z"/></svg>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700">Unpaid</div>
                    <div className="text-xs text-slate-400">{secondInstallmentData.counts.UNPAID} leads</div>
                  </div>
                </div>
                <div className="text-sm font-semibold text-rose-700">{secondInstallmentData.unpaidPct}%</div>
              </div>

              <div className="w-full h-12 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${secondInstallmentData.unpaidPct}%`,
                    background: 'linear-gradient(90deg,#ff7a7a,#ef4444)',
                    transition: 'width 700ms ease',
                    boxShadow: 'inset 0 -2px 6px rgba(0,0,0,0.04)'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 mt-4 border-t">
          <div className="text-sm text-slate-500">Last updated: <span className="font-medium text-slate-700">just now</span></div>
          <div className="flex items-center gap-3">
            <button className="px-2 py-1 text-xs transition border rounded-md border-slate-200 text-slate-700 hover:bg-slate-50" onClick={() => window.location.reload()}>Refresh</button>
          </div>
        </div>
      </div>

      {/* WARNING MESSAGE STATUS */}
      <div className="col-span-1 p-4 bg-white shadow-lg rounded-xl">
        <h3 className="mb-2 text-lg font-semibold text-black">WARNING MESSAGE STATUS</h3>

        <div className="grid items-center grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col items-center justify-center p-2">
            <div style={{ width: 160, height: 160 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={warningMessageStatus.pieSeries}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={44}
                    outerRadius={72}
                    paddingAngle={4}
                    labelLine={false}
                    label={({ name, value }) => `${name} (${value})`}
                  >
                    {warningMessageStatus.pieSeries.map((entry, idx) => (
                      <Cell key={`wm-pie-${idx}`} fill={entry.name === 'Pending' ? '#b82727' : '#10b981'} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, name) => [v, name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-2 text-sm text-center text-slate-600">
              <div>Total: <span className="font-semibold text-slate-800">{warningMessageStatus.total}</span></div>
            </div>
          </div>

          <div className="p-2">
            <div className="mb-2 text-sm text-slate-500">Sent — last {warningMessageStatus.days} days</div>
            <div style={{ width: '100%', height: 140 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={warningMessageStatus.trend} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <defs>
                    <linearGradient id="wmArea" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                  <YAxis hide allowDecimals={false} />
                  <Tooltip formatter={(v, name) => [v, name]} />
                  <Area type="monotone" dataKey="SENT" stroke="#059669" fill="url(#wmArea)" strokeWidth={2} dot={{ r: 3 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="flex items-center justify-between mt-3 text-sm text-slate-600">
              <div>Not Sent: <span className="font-semibold text-amber-700">{warningMessageStatus.pending}</span></div>
              <div>Sent: <span className="font-semibold text-green-700">{warningMessageStatus.sent}</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* STUDENT STATUS */}
      <div className="col-span-1 p-4 border shadow-xl bg-white/95 rounded-2xl border-slate-100">
        <h3 className="mb-3 text-lg font-semibold text-black">STUDENT STATUS</h3>

        <div className="grid items-center grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-indigo-50">
              <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-md">
                <svg className="w-5 h-5 text-indigo-600" viewBox="0 0 20 20" fill="currentColor"><path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5z"/></svg>
              </div>
              <div>
                <div className="text-xs text-slate-500">Total Students</div>
                <div className="text-lg font-bold text-slate-800">{studentStatusData.total}</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50">
              <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-md">
                <svg className="w-5 h-5 text-green-600" viewBox="0 0 20 20" fill="currentColor"><path d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293A1 1 0 103.293 10.707l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"/></svg>
              </div>
              <div>
                <div className="text-xs text-slate-500">Active</div>
                <div className="text-lg font-semibold text-green-700">{studentStatusData.counts.active}</div>
                <div className="text-xs text-slate-400">{studentStatusData.pct.active}%</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-rose-50">
              <div className="flex items-center justify-center w-10 h-10 rounded-md bg-rose-100">
                <svg className="w-5 h-5 text-rose-600" viewBox="0 0 20 20" fill="currentColor"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 7h2v5H9V7zM9 13h2v2H9v-2z"/></svg>
              </div>
              <div>
                <div className="text-xs text-slate-500">Inactive</div>
                <div className="text-lg font-semibold text-rose-700">{studentStatusData.counts.inactive}</div>
                <div className="text-xs text-slate-400">{studentStatusData.pct.inactive}%</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="relative w-44 h-44">
              <div style={{ width: 176, height: 176 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      <linearGradient id="ssActive" x1="0" x2="1">
                        <stop offset="0%" stopColor="#34d399" />
                        <stop offset="100%" stopColor="#059669" />
                      </linearGradient>
                      <linearGradient id="ssInactive" x1="0" x2="1">
                        <stop offset="0%" stopColor="#ff0000" />
                        <stop offset="100%" stopColor="#b82727" />
                      </linearGradient>
                    </defs>

                    <Pie
                      data={studentStatusData.pie}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={56}
                      outerRadius={84}
                      cornerRadius={8}
                      paddingAngle={4}
                      isAnimationActive={true}
                    >
                      {studentStatusData.pie.map((entry, i) => (
                        <Cell key={`ss-${i}`} fill={entry.name === 'Active' ? 'url(#ssActive)' : 'url(#ssInactive)'} stroke="#fff" strokeWidth={1} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v,name) => [v,name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-2xl font-extrabold text-slate-800">{studentStatusData.pct.active}%</div>
                <div className="text-xs text-slate-500">Active</div>
              </div>
            </div>
          </div>

          <div className="w-full space-y-3">
            <div>
              <div className="mb-1 text-xs text-slate-500">Active — last {studentStatusData.days} days</div>
              <div style={{ width: '100%', height: 80 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={studentStatusData.trend} margin={{ top: 6, right: 8, left: 0, bottom: 6 }}>
                    <defs>
                      <linearGradient id="ssArea" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity={0.36}/>
                        <stop offset="100%" stopColor="#34d399" stopOpacity={0.04}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
                    <YAxis hide allowDecimals={false} />
                    <Tooltip formatter={(v,name) => [v,name]} />
                    <Area type="monotone" dataKey="ACTIVE" stroke="#059669" fill="url(#ssArea)" strokeWidth={2} dot={{ r: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div>
              <div className="mb-2 text-xs text-slate-500">Distribution</div>
              <div className="relative w-full h-10 overflow-hidden border rounded-full bg-slate-100 border-slate-200">
                <div style={{
                  width: `${studentStatusData.pct.inactive}%`,
                  height: '100%',
                  float: 'left',
                  background: 'linear-gradient(90deg,#ff0000,#b82727)',
                  transition: 'width 600ms ease'
                }} />
                <div style={{
                  width: `${studentStatusData.pct.active}%`,
                  height: '100%',
                  float: 'left',
                  background: 'linear-gradient(90deg,#34d399,#10b981)',
                  transition: 'width 600ms ease'
                }} />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="px-3 py-1 text-xs rounded-full bg-white/70">{studentStatusData.counts.active} Active • {studentStatusData.counts.inactive} Inactive</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>


      {error && <div className="text-red-500">{error}</div>}
    </div>
  );
}

export default DashboardPending;
